import { Component, OnInit, inject, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { UserSettings } from 'src/app/interfaces/interfaces';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { BookingsStore } from 'src/app/stores/bookings.store';
import { UserStore } from 'src/app/stores/user.store';

@Component({
  selector: 'app-booking-visibility-preferences-modal',
  templateUrl: './booking-visibility-preferences-modal.component.html',
  styleUrls: ['./booking-visibility-preferences-modal.component.scss'],
})
export class BookingVisibilityPreferencesModalComponent implements OnInit {
  private modalController = inject(ModalController);
  private userStore = inject(UserStore);
  private bookingsStore = inject(BookingsStore);
  private toastService = inject(ToastService);

  showActive = signal(true);
  showExpired = signal(true);
  isSaving = signal(false);

  ngOnInit(): void {
    const settings = this.userStore.userPreferences()?.userSettings;
    this.showActive.set(settings?.showActiveBookings !== false);
    this.showExpired.set(settings?.showExpiredBookings !== false);
  }

  onShowActiveChange(event: CustomEvent<{ checked: boolean }>): void {
    void this.saveVisibility('showActiveBookings', event);
  }

  onShowExpiredChange(event: CustomEvent<{ checked: boolean }>): void {
    void this.saveVisibility('showExpiredBookings', event);
  }

  dismissModal(): void {
    this.modalController.dismiss();
  }

  private async saveVisibility(
    field: 'showActiveBookings' | 'showExpiredBookings',
    event: CustomEvent<{ checked: boolean }>
  ): Promise<void> {
    const checked = event.detail.checked;
    const current = field === 'showActiveBookings' ? this.showActive() : this.showExpired();
    const toggle = event.target as HTMLIonToggleElement;
    if (checked === current) {
      return;
    }
    if (this.isSaving()) {
      this.setToggleChecked(toggle, current);
      return;
    }

    const settings = this.userStore.userPreferences()?.userSettings;
    if (!settings) {
      this.setToggleChecked(toggle, current);
      await this.toastService.openErrorToastAsync('settingsSomethingWentWrongToast');
      return;
    }

    const next: UserSettings = {
      ...settings,
      showActiveBookings: this.showActive(),
      showExpiredBookings: this.showExpired(),
      [field]: checked,
    };
    this.setField(field, checked);
    this.isSaving.set(true);
    try {
      await this.userStore.updateUserSettings(next);
      const saved = this.userStore.userPreferences()?.userSettings?.[field];
      if (saved !== checked) {
        this.setField(field, current);
        this.setToggleChecked(toggle, current);
        await this.toastService.openErrorToastAsync('settingsSomethingWentWrongToast');
        return;
      }
      this.bookingsStore.setTemporaryVisibility(null);
    } finally {
      this.isSaving.set(false);
    }
  }

  private setField(
    field: 'showActiveBookings' | 'showExpiredBookings',
    checked: boolean
  ): void {
    if (field === 'showActiveBookings') {
      this.showActive.set(checked);
    } else {
      this.showExpired.set(checked);
    }
  }

  private setToggleChecked(toggle: HTMLIonToggleElement, checked: boolean): void {
    if (!toggle || toggle.checked === checked) {
      return;
    }
    toggle.checked = checked;
  }
}
